/* ============================================================
   Section: Social Video Feed
   Drag carousel, hover-to-play video, YouTube lightbox, GSAP parallax
   ============================================================ */

(function () {
  'use strict';

  /* ─── Named constants (extracted magic numbers) ─── */
  var DRAG_SPEED_MULTIPLIER = 2.2;
  var DRAG_THRESHOLD = 5;
  var YT_FADE_DELAY = 800;
  var CARD_STAGGER_DELAY = 400;
  var SCROLL_OBSERVER_DELAY = 500;
  var MP4_VISIBILITY_THRESHOLD = 0.2;
  var YT_VISIBILITY_THRESHOLD = 0.3;
  var SECTION_VISIBILITY_THRESHOLD = 0.15;
  var MOMENTUM_FRICTION = 0.92;
  var MOMENTUM_MIN_VELOCITY = 0.5;
  var MOMENTUM_INTERVAL = 16;

  /* ─── Per-section observer tracking for cleanup ─── */
  var sectionObservers = {};
  var sectionListeners = {};

  /**
   * Clean up a section before re-initialization.
   * Disconnects observers, removes tracked listeners, kills GSAP ScrollTriggers.
   */
  function cleanupSection(section) {
    var sectionId = section.dataset.sectionId;
    if (!sectionId) return;

    // Disconnect all IntersectionObservers for this section
    if (sectionObservers[sectionId]) {
      sectionObservers[sectionId].forEach(function (obs) {
        obs.disconnect();
      });
      delete sectionObservers[sectionId];
    }

    // Remove tracked document-level event listeners
    if (sectionListeners[sectionId]) {
      sectionListeners[sectionId].forEach(function (entry) {
        entry.target.removeEventListener(entry.event, entry.handler, entry.options);
      });
      delete sectionListeners[sectionId];
    }

    // Kill GSAP ScrollTriggers scoped to this section
    if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach(function (st) {
        if (st.trigger && section.contains(st.trigger)) {
          st.kill();
        }
      });
    }
  }

  /**
   * Register an IntersectionObserver for cleanup tracking.
   */
  function trackObserver(sectionId, observer) {
    if (!sectionObservers[sectionId]) sectionObservers[sectionId] = [];
    sectionObservers[sectionId].push(observer);
  }

  /**
   * Register a document-level event listener for cleanup tracking.
   */
  function trackListener(sectionId, target, event, handler, options) {
    if (!sectionListeners[sectionId]) sectionListeners[sectionId] = [];
    sectionListeners[sectionId].push({ target: target, event: event, handler: handler, options: options });
  }

  /**
   * Initialize a single Social Video Feed section by its DOM element.
   * Called on page load and on shopify:section:load for editor support.
   */
  function initSection(section) {
    if (!section || section.dataset.svfInitialized) return;
    section.dataset.svfInitialized = 'true';

    var sectionId = section.dataset.sectionId;
    var modal = document.getElementById('svf-modal-' + sectionId);
    if (!modal) return;

    /* ─── Drag-to-scroll (mouse + touch + momentum) ─── */
    var track = section.querySelector('.svf-track');
    if (track) {
      var isDragging = false;
      var hasDragged = false;
      var startX = 0;
      var scrollLeft = 0;
      var lastDelta = 0;
      var lastMoveTime = 0;
      var momentumId = null;

      function dragStart(pageX) {
        if (momentumId) { clearInterval(momentumId); momentumId = null; }
        isDragging = true;
        hasDragged = false;
        lastDelta = 0;
        lastMoveTime = Date.now();
        track.classList.add('is-grabbing');
        startX = pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
      }

      function dragMove(pageX, evt) {
        if (!isDragging) return;
        if (evt) evt.preventDefault();
        var x = pageX - track.offsetLeft;
        var delta = x - startX;
        if (Math.abs(delta) > DRAG_THRESHOLD) hasDragged = true;
        track.scrollLeft = scrollLeft - delta * DRAG_SPEED_MULTIPLIER;

        // Track delta and time for momentum calculation
        lastDelta = delta;
        lastMoveTime = Date.now();
      }

      function dragEnd() {
        if (!isDragging) return;
        isDragging = false;
        track.classList.remove('is-grabbing');

        // Momentum / deceleration after release
        // Velocity derived from total drag delta, scaled down for smooth coast
        var velocity = -lastDelta * 0.3;

        if (Math.abs(velocity) > MOMENTUM_MIN_VELOCITY) {
          momentumId = setInterval(function () {
            velocity *= MOMENTUM_FRICTION;
            track.scrollLeft += velocity;
            if (Math.abs(velocity) < MOMENTUM_MIN_VELOCITY) {
              clearInterval(momentumId);
              momentumId = null;
            }
          }, MOMENTUM_INTERVAL);
        }
      }

      // Mouse events
      track.addEventListener('mousedown', function (e) {
        dragStart(e.pageX);
      });

      var mouseMoveHandler = function (e) { dragMove(e.pageX, e); };
      var mouseUpHandler = function () { dragEnd(); };
      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);
      trackListener(sectionId, document, 'mousemove', mouseMoveHandler);
      trackListener(sectionId, document, 'mouseup', mouseUpHandler);

      // Touch events
      track.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) return;
        dragStart(e.touches[0].pageX);
      }, { passive: true });

      track.addEventListener('touchmove', function (e) {
        if (e.touches.length !== 1) return;
        dragMove(e.touches[0].pageX, e);
      }, { passive: false });

      track.addEventListener('touchend', function () {
        dragEnd();
      });
    }

    /* ─── Hover play/pause videos ─── */
    var cards = section.querySelectorAll('.svf-card__trigger');
    var activeVideos = []; // Track playing videos for cleanup

    cards.forEach(function (btn) {
      var video = btn.querySelector('.svf-card__video');
      if (!video) return;

      // Mark video as loaded when it has data
      var markLoaded = function() {
        video.classList.add('has-loaded');
      };
      
      if (video.readyState >= 2) {
        // Already has enough data
        markLoaded();
      } else {
        video.addEventListener('loadeddata', markLoaded, { once: true });
      }

      btn.addEventListener('mouseenter', function () {
        if (!video.paused) return; // Already playing
        video.play().catch(function () {});
        if (!activeVideos.includes(video)) {
          activeVideos.push(video);
        }
      });

      btn.addEventListener('mouseleave', function () {
        // Don't pause if auto-playing via scroll
        if (btn.classList.contains('is-autoplaying')) return;
        video.pause();
        video.currentTime = 0;
        var idx = activeVideos.indexOf(video);
        if (idx > -1) activeVideos.splice(idx, 1);
      });
    });

    /* Pause all videos when modal opens */
    function pauseAllVideos() {
      activeVideos.forEach(function(video) {
        video.pause();
        video.currentTime = 0;
      });
      activeVideos = [];
    }

    /* ─── mp4 preview auto-play (for cards with local video) ─── */
    if ('IntersectionObserver' in window) {
      var videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var btn = entry.target;
            var video = btn.querySelector('.svf-card__video');
            if (!video) return;
            if (entry.isIntersecting) {
              btn.classList.add('is-autoplaying');
              video.play().catch(function () {});
              if (!activeVideos.includes(video)) {
                activeVideos.push(video);
              }
            } else {
              btn.classList.remove('is-autoplaying');
              video.pause();
              video.currentTime = 0;
              var idx = activeVideos.indexOf(video);
              if (idx > -1) activeVideos.splice(idx, 1);
            }
          });
        },
        { threshold: MP4_VISIBILITY_THRESHOLD }
      );
      trackObserver(sectionId, videoObserver);
      cards.forEach(function (btn) {
        if (btn.querySelector('.svf-card__video')) {
          videoObserver.observe(btn);
        }
      });
    }

    /* ─── Lightbox Modal ─── */
    var iframe = modal.querySelector('.svf-modal__iframe');
    var iframeWrap = modal.querySelector('.svf-modal__iframe-wrap');
    var modalInner = modal.querySelector('.svf-modal__inner');
    var closeBtn = modal.querySelector('.svf-modal__close');
    var backdrop = modal.querySelector('.svf-modal__backdrop');
    var previousFocus = null;

    function openModal(youtubeId, title, isShort) {
      if (!youtubeId) return;

      // Pause all preview videos
      pauseAllVideos();

      // Portrait (Shorts) vs wide (regular) ratio
      iframeWrap.classList.toggle('is-wide', !isShort);
      modalInner.classList.toggle('is-portrait', !!isShort);

      iframe.src =
        'https://www.youtube.com/embed/' +
        youtubeId +
        '?autoplay=1&rel=0&playsinline=1';
      if (title) iframe.title = title;

      previousFocus = document.activeElement;
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';

      // Trigger open animation next frame
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('is-open');
          closeBtn.focus();
        });
      });
    }

    function closeModal() {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';

      setTimeout(function () {
        modal.setAttribute('hidden', '');
        iframe.src = '';
        if (previousFocus) previousFocus.focus();
      }, 350);
    }

    cards.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Don't open modal if user was dragging the carousel
        if (hasDragged) {
          hasDragged = false;
          return;
        }
        var ytId = btn.dataset.youtubeId;
        var ytTitle = btn.dataset.youtubeTitle;
        var isShort = btn.dataset.isShort === 'true';
        openModal(ytId, ytTitle, isShort);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);

    var keydownHandler = function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    };
    document.addEventListener('keydown', keydownHandler);
    trackListener(sectionId, document, 'keydown', keydownHandler);

    /* ─── GSAP ScrollTrigger ─── */
    function initGSAP() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        fallbackEntrance();
        return;
      }

      // Respect reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        section.querySelectorAll('.svf-card').forEach(function (c) {
          c.style.opacity = '1';
          c.style.transform = 'none';
        });
        section.querySelectorAll('.svf-heading-line').forEach(function (l) {
          l.style.opacity = '1';
          l.style.transform = 'none';
        });
        startScrollObserver();
        return;
      }

      try {
        gsap.registerPlugin(ScrollTrigger);
      } catch (e) {
        console.warn('ScrollTrigger registration failed:', e);
        fallbackEntrance();
        return;
      }

      // ── Heading lines — scrub-based stacking entrance ──
      // Each line slides in from alternating directions as section enters viewport
      var headingZone = section.querySelector('.svf-heading-zone');
      var headingLines = section.querySelectorAll('.svf-heading-line');
      if (headingZone && headingLines.length) {
        // Alternate directions: odd lines from right, even from left
        var lineConfigs = [
          { x: 200, rotate: 3 },    // line 1: from right
          { x: -200, rotate: -2 },   // line 2: from left
          { x: 160, rotate: 2 },     // line 3: from right
          { x: -160, rotate: -3 }    // line 4: from left
        ];

        headingLines.forEach(function (line, i) {
          var cfg = lineConfigs[i] || lineConfigs[0];
          gsap.fromTo(line,
            { opacity: 0, x: cfg.x, scale: 0.85, rotation: cfg.rotate },
            {
              opacity: 1,
              x: 0,
              scale: 1,
              rotation: 0,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: headingZone,
                start: 'top+=' + (i * 60) + ' 85%',
                end: 'top+=' + (i * 60 + 200) + ' 85%',
                scrub: 0.6,
              }
            }
          );
        });
      }

      // Dramatic waterfall cascade — cards drop from progressively lower heights
      // Creates a true "falling into a scattered pile" stacking effect
      var trackOuter = section.querySelector('.svf-track-outer');
      var allCards = section.querySelectorAll('.svf-card');
      
      allCards.forEach(function (card, i) {
        // First cards drop from way higher, each subsequent card from progressively lower
        // Creates dramatic cascade: 220px → 190px → 160px → 130px... → 40px minimum
        var startY = Math.max(220 - (i * 25), 40);
        
        gsap.fromTo(card,
          { opacity: 0, y: startY, scale: 0.88, rotation: (i % 2 === 0 ? 8 : -8) },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotation: 0,
            ease: 'back.out(1.7)',  // More dramatic bounce with overshoot
            scrollTrigger: {
              trigger: trackOuter,
              start: 'top+=' + (i * 150) + ' 105%',  // Start earlier - videos begin before section fully in view
              end: 'top+=' + (i * 150 + 300) + ' 105%',  // Longer animation distance
              scrub: 1,  // Slower scrub for more visible cascade
              toggleActions: 'play none none reverse',
              // markers: true  // Uncomment to debug trigger points
            }
          }
        );
      });

      // Simplified parallax heading lines - better performance
      section.querySelectorAll('[data-svf-parallax]').forEach(function (el) {
        var speed = parseFloat(el.dataset.svfParallax) || -0.1;
        gsap.to(el, {
          x: function () {
            return window.innerWidth * speed;
          },
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.8,
            invalidateOnRefresh: true
          },
        });
      });
    }

    /* Fallback entrance when GSAP is unavailable */
    function fallbackEntrance() {
      // Respect reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        section.querySelectorAll('.svf-card').forEach(function (c) {
          c.style.opacity = '1';
          c.style.transform = 'none';
        });
        section.querySelectorAll('.svf-heading-line').forEach(function (l) {
          l.style.opacity = '1';
          l.style.transform = 'none';
        });
        return;
      }

      // Trigger CSS fallback animation on heading lines
      section.querySelectorAll('.svf-heading-line').forEach(function (l) {
        l.classList.add('svf-heading--fallback-anim');
      });

      // Use IntersectionObserver for scroll-triggered entrance
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                var allCards = section.querySelectorAll('.svf-card');
                allCards.forEach(function (c, i) {
                  setTimeout(function () {
                    c.classList.add('svf-card--visible');
                  }, i * (CARD_STAGGER_DELAY * 0.6)); // Faster stagger in fallback
                });
                observer.disconnect();
              }
            });
          },
          { threshold: SECTION_VISIBILITY_THRESHOLD }
        );
        trackObserver(sectionId, observer);
        observer.observe(section);
      } else {
        // Final fallback: just show them
        section.querySelectorAll('.svf-card').forEach(function (c) {
          c.classList.add('svf-card--visible');
        });
      }
    }

    /* Use theme's lazy-loaded GSAP instead of loading a duplicate */
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      // GSAP already loaded
      initGSAP();
    } else {
      // Wait for theme.liquid's lazy GSAP loader
      var gsapCheckAttempts = 0;
      var maxAttempts = 50; // 10 seconds max wait
      
      var checkGSAP = setInterval(function() {
        gsapCheckAttempts++;
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(checkGSAP);
          initGSAP();
        } else if (gsapCheckAttempts >= maxAttempts) {
          clearInterval(checkGSAP);
          console.warn('GSAP not loaded, using fallback animations');
          fallbackEntrance();
        }
      }, 200);

      // Also listen for the gsap:loaded event from theme.liquid
      window.addEventListener('gsap:loaded', function() {
        if (gsapCheckAttempts < maxAttempts) {
          clearInterval(checkGSAP);
          setTimeout(initGSAP, 100); // Small delay to ensure ScrollTrigger is ready
        }
      }, { once: true });
    }
  }

  /* ─── Auto-init on page load ─── */
  function initAll() {
    document.querySelectorAll('.svf-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ─── Shopify theme editor support ─── */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('.svf-section');
    if (section) {
      cleanupSection(section);
      section.removeAttribute('data-svf-initialized');
      initSection(section);
    }
  });
})();
